import {
  ArrowDropDown,
  DarkModeRounded,
  InventoryOutlined,
  KeyboardDoubleArrowLeft,
  LightModeRounded,
  Logout,
  Menu as MenuIcon,
  MoneyOutlined,
  Person2Outlined,
  RocketLaunchOutlined,
  ScreenShareOutlined,
  Sell,
} from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import { DRAWER_WIDTH } from "../config/config";
import { useThemeMode } from "../ThemeModeContext";
import { getInitials } from "../utils/StringTool";
import { useDashboard } from "./hooks/useDashboard";

const Dashboard: React.FC = () => {
  const {
    user,
    userData,
    permittedPaths,
    isMobile,
    mobileOpen,
    dialogOpen,
    location,
    theme,
    open,
    anchorEl,
    notifications,
    userMenuOpen,
    userMenuAnchor,
    handleUserMenuOpen,
    handleUserMenuClose,
    handleOpen,
    handleClose,
    setMobileOpen,
    setDialogOpen,
    handleDrawerToggle,
    handleLogout,
  } = useDashboard();
  const { mode, toggleMode } = useThemeMode();

  const [collapsed, setCollapsed] = useState(false);
  const drawerWidth = collapsed ? 72 : DRAWER_WIDTH;

  const COLLAPSED_ROUTES = ["/dashboard/stations"];

  useEffect(() => {
    const shouldCollapse = COLLAPSED_ROUTES.some((path) =>
      location.pathname.startsWith(path)
    );
    setCollapsed(shouldCollapse);
  }, [location.pathname]);

  const menuGroups = [
    {
      title: "Inicio",
      items: [
        {
          text: "Dashboard",
          icon: <RocketLaunchOutlined />,
          path: "/dashboard",
        },
      ],
    },
    // {
    //   title: "Administración",
    //   items: [
    //     {
    //       text: "Reporte de ventas",
    //       icon: <AssessmentOutlined />,
    //       path: "/dashboard/reports/sales",
    //     },
    //   ],
    // },
    {
      title: "Módulos",
      items: [
        {
          text: "Caja",
          icon: <MoneyOutlined />,
          path: "/dashboard/cash",
        },
        {
          text: "Módulos de venta",
          icon: <Sell />,
          path: "/dashboard/tables",
        },
        {
          text: "Pantallas",
          icon: <ScreenShareOutlined />,
          path: "/dashboard/stations",
        },
      ],
    },
    {
      title: "Control",
      items: [
        {
          text: "Productos",
          icon: <InventoryOutlined />,
          path: "/dashboard/products",
        },
        {
          text: "Clientes",
          icon: <Person2Outlined />,
          path: "/dashboard/customers",
        },
      ],
    },
  ];

  const menuProfile = (
    <Menu
      anchorEl={userMenuAnchor}
      open={userMenuOpen}
      onClose={handleUserMenuClose}
      PaperProps={{
        sx: {
          mt: 1.5,
          minWidth: 290,
          borderRadius: "20px",
          bgcolor: "background.default",
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 6px 30px rgba(0,0,0,0.08)",
          p: 0,
          overflow: "hidden",
        },
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Box
        sx={{
          px: 2,
          py: 2.4,
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: "1px solid",
          borderColor: theme.palette.divider,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            padding: 1,
          }}
        >
          <img
            src={userData!.currentCompany.imageUrl}
            alt="company-logo"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              color: "text.primary",
            }}
          >
            {userData.currentCompany.companyName}
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              opacity: 0.8,
              color: "text.secondary",
            }}
          >
            Sede: {userData.currentStore.storeName}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          px: 2,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          borderBottom: "1px solid",
          borderColor: theme.palette.divider,
        }}
      >
        <Typography
          sx={{ fontSize: 15, fontWeight: 700, color: "text.primary" }}
        >
          {userData?.fullName}
        </Typography>
        <Typography
          sx={{ fontSize: 13, opacity: 0.9, color: "text.secondary" }}
        >
          {userData?.email}
        </Typography>
        {userData!.role!.length > 0 && (
          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {userData.role.map((role) => (
              <Box
                key={role}
                sx={{
                  px: 1.4,
                  py: 0.6,
                  borderRadius: "10px",
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: "rgba(0,0,0,0.06)",
                  color: "text.primary",
                  textTransform: "uppercase",
                }}
              >
                {role}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <MenuItem
        sx={{
          py: 1.8,
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: "16px",
          mx: 2,
          mt: 1.5,
          mb: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.2}>
          {mode === "light" ? (
            <DarkModeRounded
              fontSize="small"
              sx={{ color: "text.secondary" }}
            />
          ) : (
            <LightModeRounded
              fontSize="small"
              sx={{ color: "text.secondary" }}
            />
          )}
          <Typography fontWeight={600} fontSize={15}>
            Apariencia
          </Typography>
        </Box>
        <Switch
          size="small"
          checked={mode === "dark"}
          onChange={toggleMode}
          icon={
            <Box
              sx={{
                width: 18,
                height: 18,
                bgcolor: "white",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
              }}
            >
              🌞
            </Box>
          }
          checkedIcon={
            <Box
              sx={{
                width: 18,
                height: 18,
                bgcolor: "white",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
              }}
            >
              🌙
            </Box>
          }
          sx={{
            width: 40,
            padding: 0,
            "& .MuiSwitch-thumb": {
              width: 26,
              height: 26,
            },
            "& .MuiSwitch-track": {
              borderRadius: 30,
              backgroundColor:
                mode === "dark"
                  ? "rgba(0, 230, 118, 0.45)"
                  : "rgba(0,0,0,0.25)",
              opacity: 1,
              transition: "all 0.3s",
            },
          }}
        />
      </MenuItem>

      {/* LOGOUT */}
      <MenuItem
        onClick={() => {
          handleUserMenuClose();
          setDialogOpen(true);
        }}
        sx={{
          py: 1.8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          fontWeight: 600,
          fontSize: 15,
          color: "error.main",
          borderRadius: "16px",
          mx: 2,
          mt: 1.5,
          mb: 1,
          border: "1px solid rgba(255,0,0,0.15)",
          transition: "0.2s",
          boxShadow: "0 2px 6px rgba(255,0,0,0.12)",
          "&:hover": {
            bgcolor: "rgba(255,0,0,0.06)",
          },
        }}
      >
        <Logout fontSize="small" sx={{ color: "error.main" }} />
        Salir
      </MenuItem>
    </Menu>
  );

  const menuNotifications = (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          mt: 1.5,
          minWidth: 320,
          maxWidth: 360,
          borderRadius: 3,
          boxShadow: 4,
          p: 0,
        },
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", alignItems: "center" }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 700 }}>
          Notificaciones
        </Typography>
      </Box>
      <Divider />
      {notifications.length === 0 ? (
        <MenuItem
          disabled
          sx={{
            py: 3,
            justifyContent: "center",
            color: "text.secondary",
            opacity: 0.8,
            fontSize: 15,
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
          >
            <Typography sx={{ color: "text.secondary" }}>
              No tienes notificaciones
            </Typography>
          </Box>
        </MenuItem>
      ) : (
        notifications.map((n) => (
          <MenuItem
            key={n.id}
            sx={{ alignItems: "flex-start", gap: 1.5, py: 1.2, px: 2 }}
            onClick={handleClose}
          >
            <ListItemAvatar sx={{ minWidth: 30 }}>
              <Avatar
                sx={{
                  background: theme.palette.secondary.main,
                  width: 30,
                  height: 30,
                  fontSize: 16,
                  fontWeight: 700,
                }}
              >
                {!user?.photoURL && getInitials(userData?.fullName)}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {n.title}
                </Typography>
              }
              secondary={
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {n.subtitle}
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      float: "right",
                      color: "text.disabled",
                      fontWeight: 400,
                      ml: 2,
                    }}
                  >
                    {n.time}
                  </Typography>
                </Box>
              }
              sx={{ my: 0 }}
            />
          </MenuItem>
        ))
      )}
    </Menu>
  );

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <List sx={{ flexGrow: 1, pt: 1 }}>
        {menuGroups.map((group, idx) => {
          const visibleItems = group.items.filter(({ path }) =>
            permittedPaths.includes(path)
          );
          if (visibleItems.length === 0) return null;
          return (
            <Box key={group.title} sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  px: 2.5,
                  pt: idx === 0 ? 1 : 2,
                  pb: 0.6,
                  color: "text.secondary",
                  fontWeight: 600,
                  textTransform: "none",
                  letterSpacing: 0.5,
                  fontSize: {
                    xs: "10px",
                    sm: "11px",
                    md: "12px",
                  },
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                  display: "block",
                }}
              >
                {group.title}
              </Typography>

              {/* --- ITEMS --- */}
              {visibleItems.map(({ text, icon, path }) => {
                const isActive = location.pathname === path;

                return (
                  <ListItemButton
                    key={text}
                    component={Link}
                    to={path}
                    onClick={isMobile ? () => setMobileOpen(false) : undefined}
                    selected={isActive}
                    sx={{
                      borderRadius: "8px",
                      height: "42px",
                      mx: 1.2,
                      mb: 0.5,
                      px: 1.5,
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: isActive
                        ? "rgba(0,0,0,0.04)"
                        : "transparent",
                      transition: "all 0.2s",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.06)",
                      },
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: isActive ? "3px" : "0px",
                        height: "60%",
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "4px",
                        transition: "all 0.2s ease",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {React.cloneElement(icon, {
                        sx: {
                          fontSize: 18,
                          color: isActive
                            ? theme.palette.primary.main
                            : "text.secondary",
                        },
                      })}
                    </ListItemIcon>

                    <ListItemText
                      primary={text}
                      sx={{
                        opacity: collapsed ? 0 : 1,
                        transition: "opacity 0.2s",
                      }}
                      slotProps={{
                        primary: {
                          sx: {
                            fontSize: "14px",
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? "text.primary" : "text.secondary",
                          },
                        },
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </Box>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }} className="no-print">
      <ConfirmationDialog
        open={dialogOpen}
        title="Confirmar cierre de sesión"
        message="¿Estás seguro de que deseas cerrar sesión?"
        onClose={() => setDialogOpen(false)}
        onConfirm={handleLogout}
        confirmText="Cerrar Sesión"
      />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
          ml: isMobile ? 0 : `${drawerWidth}px`,
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "transparent",
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="secondary"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ width: 10 }} />
          <Tooltip title="Cuenta">
            <Box
              onClick={handleUserMenuOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                px: 1.2,
                py: 0.7,
                borderRadius: "50px",
                transition: "0.2s",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Box
                component="img"
                src={userData?.currentCompany.imageUrl}
                alt="Logo empresa"
                sx={{
                  width: 26,
                  height: 26,
                  padding: 0.5,
                  borderRadius: "50%",
                  objectFit: "cover",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  fontSize: "10px",
                }}
              >
                {userData?.currentStore.storeName}
              </Typography>
              <ArrowDropDown
                sx={{
                  color: "text.primary",
                  fontSize: 22,
                  opacity: 0.8,
                }}
              />
            </Box>
          </Tooltip>
        </Toolbar>
      </AppBar>
      {menuProfile}
      {menuNotifications}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        sx={{
          width: drawerWidth,
          transition: "width 0.25s ease",
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            transition: "width 0.25s ease",
            boxSizing: "border-box",
            bgcolor: "background.paper",
            overflowX: "hidden",
            borderRight: "1px solid rgba(0,0,0,0.06)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 4,
            px: 2,
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Box
            component="img"
            src="/assets/icons/logo.png"
            alt="Company Logo"
            sx={{
              width: collapsed ? "35px" : "90px",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>
        {drawerContent}
        <IconButton
          onClick={() => setCollapsed(!collapsed)}
          sx={{
            position: "absolute",
            top: 5,
            right: 5,
            zIndex: 2000,
            bgcolor: "divider",
            color: "black",
            "&:hover": {
              transform: "scale(1.1)",
            },
            padding: 0.5,
            transition: "all 0.2s ease",
            borderRadius: "50%",
          }}
        >
          <KeyboardDoubleArrowLeft
            sx={{
              fontSize: 16,
              transition: "transform 0.25s ease",
              transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />{" "}
        </IconButton>
      </Drawer>
      <Box
        component="main"
        sx={{ flexGrow: 1, bgcolor: "background.default", p: 0 }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Dashboard;
